/**
 * Logo Customization Page JavaScript
 * Handles the multi-step logo customization flow
 */
(function () {
    'use strict';

    // Configuration - Initialized in init()
    let CONFIG = {};

    // State
    const state = {
        currentStep: 1,
        selectedItems: [],
        logoFile: null,
        logoUrl: null,
        uploadedLogoUrl: null, // Supabase URL
        isExistingLogo: false, // True when using a previously uploaded/paid logo from gallery
        customization: {
            mode: 'common', // 'common' | 'individual'
            commonPositions: [], // Array of position names
            itemPositions: {}, // { itemKey: [positionNames] }
            activeItemIndex: 0, // For individual mode & preview
            notes: ''
        },
        calibrationMode: false,
        drag: {
            active: false,
            isResizing: false,
            element: null,
            startX: 0,
            startY: 0,
            startTop: 0,
            startLeft: 0,
            startWidth: 0,
            containerWidth: 0,
            containerHeight: 0
        },
        manualScrollOccurred: false // Round 18: Detect manual movement to stop auto-scroll
    };

    // DOM Elements
    let elements = {};

    function cacheElements() {
        elements = {
            panels: document.querySelectorAll('[data-step]'),
            stepIndicators: document.querySelectorAll('[data-step-indicator]'),
            nextBtns: document.querySelectorAll('[data-next-step]'),
            prevBtns: document.querySelectorAll('[data-prev-step]'),
            confirmBtn: document.querySelector('[data-confirm]'),
            cartItemsContainer: document.querySelector('[data-cart-items]'),
            selectionSummary: document.querySelector('[data-selection-summary]'),
            selectedCount: document.querySelector('[data-selected-count]'),
            mockupContainer: document.querySelector('[data-mockup-container]'),
            mockupImage: document.querySelector('[data-mockup-image]'),
            logoOverlay: document.querySelector('[data-logo-overlay]'),
            logoPreview: document.querySelector('[data-logo-preview]'),
            previewSelector: document.querySelector('[data-preview-selector]'), // May not exist with new UI
            positionGrid: document.querySelector('[data-position-grid]'),
            positionGroup: document.querySelector('[data-position-group]'),
            modeSelector: document.querySelector('[data-mode-selector]'), // Container for segmented control
            currentItemLabel: document.querySelector('[data-current-item-label]'),
            summaryLogo: document.querySelector('[data-summary-logo]'),
            summaryPositions: document.querySelector('[data-summary-positions]'),
            summaryFilm: document.querySelector('[data-summary-film]'),
            summaryItems: document.querySelector('[data-summary-items]'),
            servicePrice: document.querySelector('[data-service-price]'),
            filmPriceDisplay: document.querySelector('[data-film-price-display]'),
            filmPriceLine: document.querySelector('[data-film-price-line]'),
            finalTotalPrice: document.querySelector('[data-final-total-price]'),
            successModal: document.querySelector('[data-success-modal]'),
            logoManager: document.querySelector('[data-logo-manager]'),
            maxItemsWarning: document.querySelector('[data-max-items-warning]'),
            minItemsWarning: document.querySelector('[data-min-items-warning]'),
            // Calculator Elements
            calcServiceTotal: document.querySelector('[data-calc-service-total]'),
            calcQty: document.querySelector('[data-calc-qty]'),
            calcFilmTotal: document.querySelector('[data-calc-film-total]'),
            calcGrandTotal: document.querySelector('[data-calc-grand-total]'),

            // New UI Elements
            thumbnailsContainer: document.querySelector('[data-thumbnails-container]'),
            thumbnailsScroll: document.querySelector('[data-thumbnails-scroll]'),
            commonOptions: document.querySelector('[data-common-options]'),
            individualList: document.querySelector('[data-individual-list]'),
            selectAllCheckbox: document.querySelector('[data-select-all]'),
            refreshBtn: document.querySelector('[data-refresh-cart]'),
            hotspotsContainer: document.querySelector('[data-hotspots-container]'),
            activeChipsContainer: document.querySelector('[data-active-chips]'),
            activeChipsContainer: document.querySelector('[data-active-chips]'),
            layoutWrapper: document.querySelector('[data-layout-wrapper]'),
            termsCheckbox: document.querySelector('[data-terms-checkbox]'),
            addNewLogoBtn: document.querySelector('[data-add-new-logo]')
        };
    }

    /**
     * Initialize the page
     */
    function init() {
        // Initialize Configuration from Liquid
        CONFIG = {
            CART_URL: window.Shopify?.routes?.cart || '/cart',
            CART_ADD_URL: (window.Shopify?.routes?.root || '/') + 'cart/add.js',
            SUPABASE_UPLOAD_URL: 'https://crtmzyzchtvcrkkhaipv.supabase.co/functions/v1/shopify-logo-upload',
            MOCKUPS: window.LOGO_CONFIG?.mockups || [],
            POSITIONS: window.LOGO_CONFIG?.positions || [],
            PRICES: {
                service: window.LOGO_CONFIG?.servicePrice || 0,
                film: window.LOGO_CONFIG?.filmPrice || 0
            },
            CURRENCY: window.LOGO_CONFIG?.currency || 'EUR'
        };

        console.log('[LogoCustomization] Initializing...', CONFIG);
        cacheElements(); // Cache DOM elements after they are ready
        initCartItems();

        // Inject Calibration UI (Temporary - Re-enabled for resizing feature)
        injectCalibrationUI();

        // Initial renders
        renderMockupHotspots();
        renderThumbnails();
        renderActiveChips(); // New Common Mode UI
        renderIndividualList();
        updateCalculator();

        bindEvents();
        bindLogoManagerEvents();

        // Initial button state
        updateStepButtons();

        // Init Layout Class
        updateLayoutClasses();

        // Round 16: Disable browser's automatic scroll restoration
        if ('scrollRestoration' in history) {
            history.scrollRestoration = 'manual';
        }

        // Round 18: Listen for manual scroll/interaction to cancel auto-adjustments
        const handleManualInteraction = () => {
            if (!state.manualScrollOccurred) {
                console.log('[LogoCustomization] Manual interaction detected, stopping auto-scroll.');
                state.manualScrollOccurred = true;
            }
        };
        window.addEventListener('wheel', handleManualInteraction, { passive: true });
        window.addEventListener('touchmove', handleManualInteraction, { passive: true });
        window.addEventListener('mousedown', handleManualInteraction, { passive: true });

        // Force scroll to top on load (Round 14/16)
        forceScrollToTop();
    }



    // ...

    function bindEvents() {
        console.log('[LogoCustomization] Binding events...');

        // Global Event Delegation for reliable clicks
        document.body.addEventListener('click', (e) => {
            const target = e.target;

            // Next Step
            if (target.matches('[data-next-step]') || target.closest('[data-next-step]')) {
                const btn = target.closest('[data-next-step]') || target;
                console.log('[LogoCustomization] Next Clicked. Current Step:', state.currentStep);
                
                // If button is visually disabled, shake it and highlight items that need attention
                if (btn.classList.contains('btn--disabled')) {
                    // Shake the button
                    btn.classList.remove('btn-shake');
                    void btn.offsetWidth; // Force reflow to restart animation
                    btn.classList.add('btn-shake');
                    btn.addEventListener('animationend', () => btn.classList.remove('btn-shake'), { once: true });
                    
                    // Highlight unselected cart items if on step 1
                    if (state.currentStep === 1) {
                        const items = document.querySelectorAll('.logo-customization__cart-item:not(.selected)');
                        items.forEach(item => {
                            item.classList.remove('highlight-pulse');
                            void item.offsetWidth;
                            item.classList.add('highlight-pulse');
                            item.addEventListener('animationend', () => item.classList.remove('highlight-pulse'), { once: true });
                        });
                        
                        // Scroll to first unselected item
                        const firstUnselected = document.querySelector('.logo-customization__cart-item:not(.selected)');
                        if (firstUnselected) {
                            firstUnselected.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }
                    return;
                }
                
                goToStep(state.currentStep + 1);
            }

            // Prev Step
            if (target.matches('[data-prev-step]') || target.closest('[data-prev-step]')) {
                console.log('[LogoCustomization] Prev Clicked');
                goToStep(state.currentStep - 1);
            }

            // Refresh Cart
            if (target.matches('[data-refresh-cart]') || target.closest('[data-refresh-cart]')) {
                console.log('[LogoCustomization] Refresh Clicked');
                refreshCartItems();
            }

            // Confirm
            if (target.matches('[data-confirm]')) {
                console.log('[LogoCustomization] Confirm Clicked');
                confirmCustomization();
            }

            // Add New Logo
            if (target.matches('[data-add-new-logo]') || target.closest('[data-add-new-logo]')) {
                console.log('[LogoCustomization] Add New Logo Clicked');
                window.location.reload();
            }
        });

        // Mode Selector (Change event doesn't bubble well for radios, but works for select/checkbox)
        // For Segmented Control (assuming it's inputs matches Name)
        elements.modeSelector.addEventListener('change', (e) => {
            if (e.target.name === 'customization_mode') {
                console.log('[LogoCustomization] Mode Changed:', e.target.value);
                state.customization.mode = e.target.value;

                // Toggle Visibility
                if (state.customization.mode === 'individual') {
                    if (elements.commonOptions) elements.commonOptions.classList.add('hidden');
                    if (elements.individualList) elements.individualList.classList.remove('hidden');
                    renderIndividualList();
                } else {
                    if (elements.commonOptions) elements.commonOptions.classList.remove('hidden');
                    if (elements.individualList) elements.individualList.classList.add('hidden');
                    renderActiveChips();

                    // Sync visuals
                    renderLogoOverlays(state.customization.commonPositions);
                    updateActiveHotspots(state.customization.commonPositions);
                }

                updateLayoutClasses(); // Update CSS classes

                // Update UI state (selected class on labels)
                document.querySelectorAll('.logo-customization__mode-option').forEach(opt => {
                    if (opt.contains(e.target)) opt.classList.add('selected');
                    else opt.classList.remove('selected');
                });

                updateStepButtons();
                updateCalculator();
            }
        });


        // Select All (Change event)
        if (elements.selectAllCheckbox) {
            console.log('[LogoCustomization] Select All Checkbox Found');
            elements.selectAllCheckbox.addEventListener('change', (e) => {
                console.log('[LogoCustomization] Select All Changed:', e.target.checked);
                toggleAllItems(e.target.checked);
            });
        } else {
            console.warn('[LogoCustomization] Select All Checkbox NOT Found');
            // Try delegation for select all as well?
            document.body.addEventListener('change', (e) => {
                if (e.target.matches('[data-select-all]')) {
                    console.log('[LogoCustomization] Select All Changed (Delegate):', e.target.checked);
                    toggleAllItems(e.target.checked);
                }
            });
        }

        // Terms Checkbox - Visual feedback reset
        if (elements.termsCheckbox) {
            elements.termsCheckbox.addEventListener('change', function () {
                if (this.checked) {
                    const notice = this.closest('.logo-customization__notice');
                    if (notice) notice.classList.remove('logo-customization__notice--error');
                }
            });
        }

        // Notes
        const notesInput = document.querySelector('[data-notes]');
        if (notesInput) {
            notesInput.addEventListener('input', (e) => {
                state.customization.notes = e.target.value;
            });
        }

        // Calibration Toggle
        if (elements.calibrationToggle) {
            elements.calibrationToggle.addEventListener('change', (e) => {
                state.calibrationMode = e.target.checked;
                elements.mockupContainer.classList.toggle('calibration-mode', state.calibrationMode);
                if (elements.calibrationPanel) elements.calibrationPanel.classList.toggle('hidden', !state.calibrationMode);
                console.log('Calibration Mode:', state.calibrationMode);
            });
        }

    }

    /**
     * Init Cart Items
     */
    function initCartItems() {
        const container = elements.cartItemsContainer;
        if (!container) return;

        container.innerHTML = '';

        // 1. Get Items (Filtered via Liquid)
        const rawItems = window.ELIGIBLE_ITEMS || [];

        if (rawItems.length === 0) {
            container.innerHTML = `
                <div class="logo-customization__empty">
                    <p>${window.LOGO_STRINGS.cartEmpty}</p>
                    <a href="/" class="logo-customization__btn logo-customization__btn--primary">${window.LOGO_STRINGS.continueShopping}</a>
                </div>
            `;
            return;
        }

        // 2. Group by Product ID
        const grouped = {};
        rawItems.forEach(item => {
            const pid = item.product_id;
            if (!grouped[pid]) {
                grouped[pid] = {
                    productId: pid,
                    title: item.product_title,
                    image: item.image,
                    totalQty: 0,
                    keys: [], // Line item keys
                    variants: []
                };
            }
            grouped[pid].totalQty += item.quantity;
            grouped[pid].keys.push(item.key);
            grouped[pid].variants.push(`${item.variant_title} x${item.quantity}`);
        });

        state.groupedItems = Object.values(grouped);
        state.selectedItems = []; // Clear previous selection

        // 3. Render Groups
        state.groupedItems.forEach((group, index) => {
            const itemEl = document.createElement('div');
            itemEl.className = 'logo-customization__cart-item';
            itemEl.dataset.productId = group.productId;

            // Check if already selected (persist state on refresh if possible, currently simple)
            // For now, new init clears selection

            const variantSummary = group.variants.join(', ').substring(0, 50) + (group.variants.join(', ').length > 50 ? '...' : '');

            itemEl.innerHTML = `
                <div class="logo-customization__item-checkbox">
                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                        <polyline points="20 6 9 17 4 12"/>
                    </svg>
                </div>
                <div class="logo-customization__item-image">
                    <img src="${group.image}" alt="${group.title}" loading="lazy">
                </div>
                <div class="logo-customization__item-info">
                    <p class="logo-customization__item-title">${group.title}</p>
                    <p class="logo-customization__item-variant" title="${group.variants.join('\n')}">${variantSummary}</p>
                    <span class="logo-customization__item-qty">Qtd Total: ${group.totalQty}</span>
                </div>
            `;

            itemEl.addEventListener('click', (e) => {
                // Prevent multi-click issues if needed
                toggleItemSelection(group, itemEl);
            });

            container.appendChild(itemEl);
        });
    }

    /**
     * Toggle item selection
     * @param {object} group - The grouped item object
     * @param {HTMLElement} element - The DOM element representing the item
     */
    function toggleItemSelection(group, element) {
        const index = state.selectedItems.findIndex(i => i.productId === group.productId);

        if (index === -1) {
            // Select
            state.selectedItems.push(group);
            element.classList.add('selected');
        } else {
            // Deselect
            state.selectedItems.splice(index, 1);
            element.classList.remove('selected');
        }

        // Update Select All Checkbox state
        const allSelected = state.selectedItems.length === state.groupedItems.length && state.groupedItems.length > 0;
        const selectAllCheckbox = document.querySelector('[data-select-all]');
        if (selectAllCheckbox) selectAllCheckbox.checked = allSelected;

        updateSelectionSummary();
        updateStepButtons();
        updateCalculator(); // Added to ensure calculator updates immediately
    }

    /**
     * Toggle all items
     */
    function toggleAllItems(shouldSelect) {
        const items = document.querySelectorAll('.logo-customization__cart-item');

        // Update State
        if (shouldSelect) {
            // Add all not already selected
            state.groupedItems.forEach(group => {
                const isSelected = state.selectedItems.some(i => i.productId === group.productId);
                if (!isSelected) {
                    state.selectedItems.push(group);
                }
            });
            // Update UI
            items.forEach(el => el.classList.add('selected'));
        } else {
            // Clear all
            state.selectedItems = [];
            // Update UI
            items.forEach(el => el.classList.remove('selected'));
        }

        // Run updates
        updateSelectionSummary();
        updateStepButtons();
        updateCalculator();
    }

    /**
     * Refresh Cart Items (Reloads section content)
     */
    async function refreshCartItems() {
        const btn = document.querySelector('[data-refresh-cart]');
        if (btn) btn.classList.add('loading');

        try {
            // Fetch the section to get fresh ELIGIBLE_ITEMS
            const rootUrl = window.routes?.root_url || '/';
            // Ensure no double slash if root is just /
            const url = rootUrl === '/' ? '/?section_id=logo-customization' : `${rootUrl}?section_id=logo-customization`;

            const response = await fetch(url);
            if (!response.ok) throw new Error('Network error');
            const text = await response.text();

            // Extract the script content for ELIGIBLE_ITEMS
            const match = text.match(/window\.ELIGIBLE_ITEMS\s*=\s*(\[[\s\S]*?\]);/);
            if (match && match[1]) {
                const newItems = JSON.parse(match[1]);
                window.ELIGIBLE_ITEMS = newItems;
                initCartItems();
                updateSelectionSummary();
                // Clear customization state as items might have changed
                state.customization.itemPositions = {};
                state.customization.commonPositions = [];
                state.customization.activeItemIndex = 0;
                renderActiveChips();

                // Clear Select All checkbox
                if (elements.selectAllCheckbox) elements.selectAllCheckbox.checked = false;
            }
        } catch (error) {
            console.error('Error refreshing cart:', error);
        } finally {
            if (btn) btn.classList.remove('loading');
        }
    }

    /**
     * Calculator Logic
     * Updated: Counts total customizations (items x positions)
     */
    function updateCalculator() {
        let totalCustomizations = 0;

        if (state.customization.mode === 'common') {
            // Common mode: (Number of Items) * (Number of Selected Positions)
            const totalItems = state.selectedItems.reduce((sum, item) => sum + item.totalQty, 0);
            const numPositions = state.customization.commonPositions.length;
            totalCustomizations = totalItems * numPositions;
        } else {
            // Individual mode: Sum of (Item Qty * Item Position Count) for each item
            state.selectedItems.forEach(item => {
                const positions = state.customization.itemPositions[item.productId] || [];
                totalCustomizations += (item.totalQty * positions.length);
            });
        }

        // Prices are in CENTS
        const serviceTotal = totalCustomizations * CONFIG.PRICES.service;
        const filmTotal = state.isExistingLogo ? 0 : CONFIG.PRICES.film;
        const grandTotal = serviceTotal + filmTotal;

        // Update DOM
        const totalItemsQty = state.selectedItems.reduce((sum, item) => sum + item.totalQty, 0);

        if (elements.calcQty) elements.calcQty.textContent = totalCustomizations; // Now shows total LOGOS applied, not just items
        // Ref: ${window.LOGO_STRINGS.customizationCount}
        // You might want to label this "Aplicações" instead of "Qtd" in the HTML if strictly needed,
        // but typically "Personalização (xN)" implies N applications.

        if (elements.calcServiceTotal) elements.calcServiceTotal.textContent = formatMoney(serviceTotal / 100);
        if (elements.calcFilmTotal) elements.calcFilmTotal.textContent = formatMoney(filmTotal / 100);
        if (elements.calcGrandTotal) elements.calcGrandTotal.textContent = formatMoney(grandTotal / 100);

        // Step 4 updates
        if (elements.servicePrice) elements.servicePrice.textContent = formatMoney(serviceTotal / 100);
        if (elements.filmPriceDisplay) elements.filmPriceDisplay.textContent = formatMoney(filmTotal / 100);
        if (elements.finalTotalPrice) elements.finalTotalPrice.textContent = formatMoney(grandTotal / 100);
    }


    function bindMockupEvents() {
        // Handled by dynamic renderMockupHotspots()
    }


    function updateMockupPreview(index) {
        const item = state.selectedItems[parseInt(index)];
        if (!item || !elements.mockupImage) return;

        // SKIP DYNAMIC IMAGE UPDATE for static mockup
        /*
        // Find configured mockup
        let imgSrc = CONFIG.MOCKUPS.find(m => m.trigger === 'default')?.image || '';

        if (item.product_type) {
            const match = CONFIG.MOCKUPS.find(m =>
                m.trigger !== 'default' &&
                item.product_type.toLowerCase().includes(m.trigger.toLowerCase())
            );
            if (match) imgSrc = match.image;
        }

        // Fallback to product image
        if (imgSrc.includes('logo-mockup-default') || !imgSrc) {
            imgSrc = item.image || imgSrc;
        }

        elements.mockupImage.src = imgSrc;
        elements.mockupImage.alt = item.product_title;
        */

        // Update overlay image
        if (elements.logoPreview && state.logoUrl) {
            elements.logoPreview.src = state.logoUrl;
            elements.logoOverlay.classList.remove('hidden');
            elements.logoOverlay.style.display = 'block';
        }
    }

    /**
     * Update Layout Classes
     */
    function updateLayoutClasses() {
        if (!elements.layoutWrapper) return;

        if (state.customization.mode === 'common') {
            elements.layoutWrapper.classList.add('mode-common');
            elements.layoutWrapper.classList.remove('mode-individual');
        } else {
            elements.layoutWrapper.classList.add('mode-individual');
            elements.layoutWrapper.classList.remove('mode-common');
        }
    }

    /**
     * Update selection summary
     */
    function updateSelectionSummary() {
        if (!elements.selectionSummary) return;

        const count = state.selectedItems.reduce((sum, item) => sum + item.totalQty, 0);
        // ... rest of summary logic relies on count ...

        if (count > 0) {
            elements.selectionSummary.classList.remove('hidden');
            elements.selectedCount.textContent = count;
        } else {
            elements.selectionSummary.classList.add('hidden');
        }

        // Update Select All Checkbox State
        const selectAllCheckbox = document.querySelector('[data-select-all]');
        if (selectAllCheckbox) {
            const allItems = document.querySelectorAll('.logo-customization__cart-item');
            const selectedItems = document.querySelectorAll('.logo-customization__cart-item.selected');
            selectAllCheckbox.checked = allItems.length > 0 && allItems.length === selectedItems.length;
            selectAllCheckbox.indeterminate = selectedItems.length > 0 && selectedItems.length < allItems.length;
        }

        const min = window.LOGO_CONFIG?.minItems || 3;
        const max = window.LOGO_CONFIG?.maxItems || 25;

        // Min Warning
        if (elements.minItemsWarning) {
            const minBold = elements.minItemsWarning.querySelector('[data-min-bold]');
            if (minBold) {
                // Force update text to ensure it always shows the number
                // Try to preserve unit if possible, or default to 'unidades'
                // But since we can't easily parse the original text if it's already replaced,
                // we'll assume the text AFTER the number is " unidades" or similar.
                // Best approach: Just set the number and let CSS/HTML handle surrounding if possible,
                // or hardcode the structure: "N unidades" matches Portuguese context.
                minBold.textContent = `${min} ${window.LOGO_STRINGS.units}`;
            }
            elements.minItemsWarning.classList.toggle('hidden', count === 0 || count >= min);
        }

        // Max Warning
        if (elements.maxItemsWarning) {
            const maxUnits = elements.maxItemsWarning.querySelector('[data-max-units]');
            if (maxUnits && maxUnits.textContent.includes('{{ max }}')) {
                maxUnits.textContent = maxUnits.textContent.replace(/\{\{ max \}\}/g, max);
            }
            elements.maxItemsWarning.classList.toggle('hidden', count <= max);
        }
    }

    const MOCKUP_COORDINATES = {
        // Populated dynamically from CONFIG.POSITIONS usually, but specific overrides can go here
    };

    /**
     * Render Hotspots Dynamically
     */
    function renderMockupHotspots() {
        if (!elements.hotspotsContainer) return;

        elements.hotspotsContainer.innerHTML = CONFIG.POSITIONS.map(pos => {
            return `
                <div class="logo-customization__hotspot"
                     data-position="${pos.name}"
                     data-calibration-draggable
                     style="top: ${pos.top}%; left: ${pos.left}%; width: ${pos.width || 8}%; height: ${pos.width || 8}%;" // Rough percentage based sizing
                     title="${pos.name}">
                      <div class="logo-customization__hotspot-handle" data-resize-handle></div>
                </div>
             `;
        }).join('');

        // Bind click events directly here or via delegation
        elements.hotspotsContainer.querySelectorAll('.logo-customization__hotspot').forEach(el => {
            el.addEventListener('click', (e) => {
                e.stopPropagation(); // Prevent bubbling if needed
                togglePosition(el.dataset.position);
            });
        });
    }

    /**
     * Toggle Item Selection (Group based)
     */
    // This function is now defined above, moved from its original place.

    /**
     * Logic to toggle a position
     */
    function togglePosition(name) {
        // Calibration Mode: Select triggers editing, doesn't validate limit
        if (state.calibrationMode) {
            // Just select it visually or do nothing, drag handles selection
            return;
        }

        let currentArray = [];
        const mode = state.customization.mode;
        let activeItemKey = null;

        if (mode === 'common') {
            currentArray = [...state.customization.commonPositions];
        } else {
            const item = state.selectedItems[state.customization.activeItemIndex];
            if (item) {
                activeItemKey = item.productId; // Use productId as the key for individual mode
                currentArray = [...(state.customization.itemPositions[activeItemKey] || [])];
            }
        }

        const exists = currentArray.includes(name);

        if (exists) {
            currentArray = currentArray.filter(p => p !== name);
        } else {
            // Normal Mode Limit
            if (currentArray.length >= 2) {
                alert(window.LOGO_STRINGS.maxPositionsAlert);
                return;
            }
            currentArray.push(name);
        }

        // Save State
        if (mode === 'common') {
            state.customization.commonPositions = currentArray;
            renderActiveChips();
        } else if (activeItemKey) {
            state.customization.itemPositions[activeItemKey] = currentArray;
            renderIndividualList(); // Re-render list to update chips
        }

        // Update UI
        renderLogoOverlays(currentArray);
        updateActiveHotspots(currentArray);
        updateCalculator();
    }

    /**
     * Render Logo Overlays (Multiple)
     */
    function renderLogoOverlays(positions) {
        if (!elements.mockupContainer) return;

        // Remove existing dynamic overlays
        elements.mockupContainer.querySelectorAll('.logo-customization__overlay-item').forEach(el => el.remove());

        // Also hide the legacy single overlay
        if (elements.logoOverlay) elements.logoOverlay.style.display = 'none';

        if (!positions || positions.length === 0) return;

        positions.forEach(posName => {
            const config = CONFIG.POSITIONS.find(p => p.name === posName);
            if (config) {
                const overlay = document.createElement('div');
                overlay.className = 'logo-customization__overlay-item';
                overlay.style.top = config.top + '%';
                overlay.style.left = config.left + '%';
                overlay.style.width = (config.width || 10) + '%';
                // Aspect ratio might be tricky, try to square it or match config
                // Use height same as width in % if we assume square hotspots, or use aspect-ratio css
                overlay.style.aspectRatio = '1/1';

                if (state.logoUrl) {
                    const img = document.createElement('img');
                    img.src = state.logoUrl;
                    overlay.appendChild(img);
                }

                elements.mockupContainer.appendChild(overlay);
            }
        });
    }

    function updateActiveHotspots(activePositions) {
        document.querySelectorAll('.logo-customization__hotspot').forEach(hotspot => {
            if (activePositions.includes(hotspot.dataset.position)) {
                hotspot.classList.add('active');
            } else {
                hotspot.classList.remove('active');
            }
        });
    }

    /**
     * Render Active Chips (Common Mode)
     */
    function renderActiveChips() {
        if (!elements.activeChipsContainer) return;

        if (state.customization.mode !== 'common') {
            elements.activeChipsContainer.innerHTML = '';
            return;
        }

        if (state.customization.commonPositions.length === 0) {
            elements.activeChipsContainer.innerHTML = `<span style="color: #999; font-size: 14px; font-style: italic;">${window.LOGO_STRINGS.noPositionSelected}</span>`;
            return;
        }

        elements.activeChipsContainer.innerHTML = state.customization.commonPositions.map(pos => `
            <div class="logo-customization__chip active" onclick="window.LogoApp.togglePosition('${pos}')">
                ${pos}
                <span class="logo-customization__chip-remove">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </span>
            </div>
        `).join('');
    }

    // Expose toggle for inline onclick (dirty but effective for generated HTML)
    window.LogoApp = window.LogoApp || {};
    window.LogoApp.togglePosition = togglePosition;


    /**
     * Render Thumbnails & Individual List
     */
    function renderThumbnails() {
        const container = document.querySelector('[data-thumbnails-scroll]');
        if (!container) return;

        container.innerHTML = state.selectedItems.map((item, index) => `
            <div class="logo-customization__thumbnail ${index === state.customization.activeItemIndex ? 'active' : ''}"
                 data-thumbnail-index="${index}"
                 title="${item.title}">
                <img src="${item.image}" alt="${item.title}">
            </div>
        `).join('');

        container.querySelectorAll('.logo-customization__thumbnail').forEach(el => {
            el.addEventListener('click', () => {
                const idx = parseInt(el.dataset.thumbnailIndex);
                state.customization.activeItemIndex = idx;
                updateMockupPreview(idx);
                renderThumbnails(); // Update active state
                // If in Individual mode, scroll to that item in list? Or just update mockup.
                // If in Common mode, just update comparison mockup
            });
        });
    }

    /**
     * Render Individual List (Step 3 Sidebar)
     */
    function renderIndividualList() {
        if (!elements.individualList) return;

        elements.individualList.innerHTML = '';

        state.selectedItems.forEach((group, index) => {
            const isActive = index === state.customization.activeItemIndex;
            const itemKey = group.productId;
            const positions = state.customization.itemPositions[itemKey] || [];

            const itemEl = document.createElement('div');
            itemEl.className = `logo-customization__individual-item ${isActive ? 'active' : ''}`;
            itemEl.addEventListener('click', () => {
                state.customization.activeItemIndex = index;
                renderIndividualList();
                renderLogoOverlays(state.customization.itemPositions[group.productId] || []);
                updateActiveHotspots(state.customization.itemPositions[group.productId] || []);
            });

            // Location Tags (Round 10)
            const tagsHtml = positions.length > 0
                ? `<div class="logo-customization__individual-tags">
                    ${positions.map(p => `<span class="logo-customization__individual-tag">${p}</span>`).join('')}
                   </div>`
                : '';

            itemEl.innerHTML = `
                <div class="logo-customization__individual-info">
                    <img src="${group.image}" alt="${group.title}">
                    <div>
                        <p class="logo-customization__individual-title">${group.title}</p>
                        <p class="logo-customization__individual-variant">${group.variants.length} variantes (${group.totalQty} un.)</p>
                    </div>
                </div>
                ${tagsHtml}
            `;

            elements.individualList.appendChild(itemEl);
        });
    }

    // Individual Item Toggle Logic
    window.LogoApp.toggleItemPosition = function (itemKey, posName) {
        let current = state.customization.itemPositions[itemKey] || [];

        if (current.includes(posName)) {
            current = current.filter(p => p !== posName);
        } else {
            // Optional: Limit check here if needed
            current.push(posName);
        }

        state.customization.itemPositions[itemKey] = current;

        // If this item is active, update mockup
        const itemIndex = state.selectedItems.findIndex(i => i.productId === itemKey);
        if (itemIndex !== -1) {
            state.customization.activeItemIndex = itemIndex;
            renderLogoOverlays(current);
            updateActiveHotspots(current);
            renderThumbnails(); // Update active thumb
        }

        renderIndividualList();
        updateCalculator();
    };

    window.LogoApp.scrollToItem = function (index) {
        state.customization.activeItemIndex = parseInt(index);
        renderLogoOverlays(state.customization.itemPositions[state.selectedItems[index].productId] || []);
        updateActiveHotspots(state.customization.itemPositions[state.selectedItems[index].productId] || []);
        renderThumbnails();
    };



    /**
     * Bind logo manager events
     */
    function bindLogoManagerEvents() {
        if (!elements.logoManager) return;

        elements.logoManager.addEventListener('logo:selected', (e) => {
            state.logoFile = e.detail.file;
            state.isExistingLogo = false; // New upload = not existing
            if (e.detail.preview) {
                state.logoUrl = e.detail.preview;
            }
            updateStepButtons();
        });

        elements.logoManager.addEventListener('logo:removed', () => {
            state.logoFile = null;
            state.logoUrl = null;
            state.uploadedLogoUrl = null;
            state.isExistingLogo = false;
            updateStepButtons();
        });

        elements.logoManager.addEventListener('logo:use-saved', (e) => {
            state.logoUrl = e.detail.url;
            state.uploadedLogoUrl = e.detail.url;
            state.isExistingLogo = true; // Metafield logo = already in system
            state.logoFile = null;
            updateStepButtons();
        });

        // NEW: Handle selection from Supabase gallery (existing logos)
        elements.logoManager.addEventListener('logo:use-existing', (e) => {
            console.log('[LogoCustomization] Existing logo selected:', e.detail);
            state.logoUrl = e.detail.url;
            state.uploadedLogoUrl = e.detail.url; // Already in Supabase
            state.isExistingLogo = true; // Skip film product on confirmation
            state.logoFile = null; // No file to upload
            updateStepButtons();
        });
    }

    /**
     * Navigate steps
     */
    function goToStep(step) {
        console.log(`[LogoCustomization] goToStep called. Target: ${step}, Current: ${state.currentStep}`);
        if (step < 1 || step > 4) return;

        // Reset interaction flag at the start of transition (Round 18)
        state.manualScrollOccurred = false;

        // Immediate scroll jump to prevent intermediate jumps (Round 16)
        forceScrollToTop();

        if (step > state.currentStep) {
            const valid = validateStep(state.currentStep);
            console.log(`[LogoCustomization] Validating Step ${state.currentStep}: ${valid}`);
            if (!valid) return;
        }

        state.currentStep = step;

        elements.panels.forEach(panel => {
            const panelStep = parseInt(panel.dataset.step);
            panel.classList.toggle('active', panelStep === step);
        });

        elements.stepIndicators.forEach(indicator => {
            const indicatorStep = parseInt(indicator.dataset.stepIndicator);
            indicator.classList.remove('active', 'completed');
            if (indicatorStep === step) indicator.classList.add('active');
            else if (indicatorStep < step) indicator.classList.add('completed');
        });

        if (step === 3) {
            initEditorStep();
            updateCalculator();
        } else if (step === 4) {
            initConfirmStep();
        }

        // RESET SCROLL: Ensure the very top is visible (Round 14)
        forceScrollToTop();

        // Update Calibration Visibility
        updateCalibrationVisibility();
    }

    function updateCalibrationVisibility() {
        if (!elements.calibrationToggle) return;

        // Only show in Step 3
        if (state.currentStep === 3) {
            elements.calibrationToggle.style.display = 'inline-block';
        } else {
            elements.calibrationToggle.style.display = 'none';
            // Force disable mode if leaving step 3
            if (state.calibrationMode) {
                state.calibrationMode = false;
                if (elements.mockupContainer) elements.mockupContainer.classList.remove('calibration-mode');
                if (elements.calibrationPanel) elements.calibrationPanel.classList.add('hidden');
                disableDragEvents();
                // Find checkbox/toggle and uncheck it
                if (elements.calibrationToggle) elements.calibrationToggle.checked = false; // It's a button now, so no checked state to visual
                if (elements.calibrationToggle.type === 'checkbox') elements.calibrationToggle.checked = false;
            }
        }
    }

    /**
     * Validation
     */
    function validateStep(step) {
        switch (step) {
            case 1:
                const count = state.selectedItems.reduce((sum, item) => sum + item.totalQty, 0);
                const min = window.LOGO_CONFIG?.minItems || 3;
                const max = window.LOGO_CONFIG?.maxItems || 25;

                if (state.selectedItems.length === 0) {
                    alert(window.LOGO_STRINGS.selectAtLeastOne);
                    return false;
                }
                if (count < min) {
                    alert(window.LOGO_STRINGS.minItemsAlert.replace(/\{\{\s*min\s*\}\}|\[min\]/g, min));
                    return false;
                }
                if (count > max) {
                    alert(window.LOGO_STRINGS.maxItemsAlertContact.replace(/\{\{\s*max\s*\}\}|\[max\]/g, max));
                    return false;
                }
                return true;
            case 2:
                if (!state.logoFile && !state.logoUrl) {
                    alert(window.LOGO_STRINGS.uploadLogoAlert);
                    return false;
                }
                return true;
            case 3:
                // Validate positions
                if (state.customization.mode === 'common') {
                    if (state.customization.commonPositions.length === 0) {
                        alert(window.LOGO_STRINGS.selectPositionAlert);
                        return false;
                    }
                } else {
                    // Check if all items have positions
                    for (let item of state.selectedItems) {
                        const poses = state.customization.itemPositions[item.productId] || [];
                        if (poses.length === 0) {
                            alert(window.LOGO_STRINGS.itemPositionAlert + item.title);
                            // Switch to view that item
                            state.customization.activeItemIndex = state.selectedItems.indexOf(item);
                            // elements.previewSelector.value = state.customization.activeItemIndex; // This is for old dropdown
                            renderIndividualList(); // Re-render to highlight the item
                            renderLogoOverlays(state.customization.itemPositions[item.productId] || []);
                            updateActiveHotspots(state.customization.itemPositions[item.productId] || []);
                            return false;
                        }
                    }
                }
                return true;
            default:
                return true;
        }
    }

    function updateStepButtons() {
        const step1NextBtn = document.querySelector('[data-step="1"] [data-next-step]');
        const step2NextBtn = document.querySelector('[data-step="2"] [data-next-step]');

        const count = state.selectedItems.reduce((sum, item) => sum + item.totalQty, 0);
        const min = window.LOGO_CONFIG?.minItems || 3;
        const max = window.LOGO_CONFIG?.maxItems || 25;

        // Strict validation for Step 1 button state (using class instead of disabled for click feedback)
        if (step1NextBtn) {
            const isValid = state.selectedItems.length > 0 && count >= min && count <= max;
            step1NextBtn.classList.toggle('btn--disabled', !isValid);
        }

        if (step2NextBtn) {
            const logoValid = !!(state.logoFile || state.logoUrl);
            step2NextBtn.classList.toggle('btn--disabled', !logoValid);
        }
    }

    /**
     * Step 3: Editor
     */
    function initEditorStep() {
        if (elements.previewSelector) {
            const options = state.selectedItems.map((item, i) =>
                `<option value="${i}">${item.title}</option>`
            ).join('');
            elements.previewSelector.innerHTML = options;
        }

        if (state.selectedItems.length > 0) {
            // Default select 0
            state.customization.activeItemIndex = 0;
            elements.previewSelector.value = 0;
            updateMockupPreview(0);
        }

        // renderPositionGrid(); // Replaced by hotspots and chips
    }



    /**
     * Calculator Logic
     * Updated: Counts total customizations (items x positions)
     */
    function updateCalculator() {
        let totalCustomizations = 0;

        if (state.customization.mode === 'common') {
            // Common mode: (Number of Items) * (Number of Selected Positions)
            const totalItems = state.selectedItems.reduce((sum, item) => sum + item.totalQty, 0);
            const numPositions = state.customization.commonPositions.length;
            totalCustomizations = totalItems * numPositions;
        } else {
            // Individual mode: Sum of (Item Qty * Item Position Count) for each item
            state.selectedItems.forEach(item => {
                const positions = state.customization.itemPositions[item.productId] || [];
                totalCustomizations += (item.totalQty * positions.length);
            });
        }

        // Prices are in CENTS
        const serviceTotal = totalCustomizations * CONFIG.PRICES.service;
        const filmTotal = state.isExistingLogo ? 0 : CONFIG.PRICES.film;
        const grandTotal = serviceTotal + filmTotal;

        // Update DOM
        const totalItemsQty = state.selectedItems.reduce((sum, item) => sum + item.totalQty, 0);

        if (elements.calcQty) elements.calcQty.textContent = totalCustomizations;

        if (elements.calcServiceTotal) elements.calcServiceTotal.textContent = formatMoney(serviceTotal / 100);
        if (elements.calcFilmTotal) elements.calcFilmTotal.textContent = formatMoney(filmTotal / 100);
        if (elements.calcGrandTotal) elements.calcGrandTotal.textContent = formatMoney(grandTotal / 100);

        // Step 4 updates
        if (elements.servicePrice) elements.servicePrice.textContent = formatMoney(serviceTotal / 100);
        if (elements.filmPriceDisplay) elements.filmPriceDisplay.textContent = formatMoney(filmTotal / 100);
        if (elements.finalTotalPrice) elements.finalTotalPrice.textContent = formatMoney(grandTotal / 100);

        // Update Step 4 Notes about service cost calculation
        const note = document.querySelector('.logo-customization__price-line--note');
        if (note) {
            note.innerHTML = `<span>(${totalCustomizations} ${window.LOGO_STRINGS.applications} x ${formatMoney(CONFIG.PRICES.service / 100)})</span>`;
        }
    }

    function formatMoney(amount) {
        return amount.toLocaleString('pt-PT', { style: 'currency', currency: CONFIG.CURRENCY });
    }

    /**
     * Step 4: Confirm
     */
    function initConfirmStep() {
        // Summary Logo
        if (elements.summaryLogo && state.logoUrl) {
            elements.summaryLogo.innerHTML = `<img src="${state.logoUrl}" alt="Logo">`;
        }

        // Positions Summary
        if (elements.summaryPositions) {
            if (state.customization.mode === 'common') {
                const posStr = state.customization.commonPositions.join(', ');
                elements.summaryPositions.innerHTML = `<p><strong>${window.LOGO_STRINGS.summaryAll}</strong> ${posStr}</p>`;
            } else {
                let html = `<strong>${window.LOGO_STRINGS.summaryPerItem}</strong><ul style="list-style:none; padding:0; margin-top:4px;">`;
                state.selectedItems.forEach(item => {
                    const poses = (state.customization.itemPositions[item.productId] || []).join(', ');
                    html += `<li style="font-size:13px; margin-bottom:2px;">• ${item.title}: ${poses}</li>`;
                });
                html += '</ul>';
                elements.summaryPositions.innerHTML = html;
            }
        }

        // Film
        if (elements.summaryFilm) {
            elements.summaryFilm.classList.remove('hidden');
        }

        // Items List
        if (elements.summaryItems) {
            const html = state.selectedItems.map(item => `
                 <div class="logo-customization__summary-item">
                    <div class="logo-customization__summary-item-image">
                        <img src="${item.image || ''}" alt="${item.title}">
                    </div>
                    <div>
                        <p class="logo-customization__summary-item-title">${item.title}</p>
                        <p class="logo-customization__summary-item-qty">Qty: ${item.totalQty}</p>
                    </div>
                </div>
            `).join('');
            elements.summaryItems.innerHTML = html;
        }

        updateCalculator();
    }

    /**
     * Upload logo to Supabase
     */
    async function uploadLogoToSupabase(file) {
        const formData = new FormData();
        formData.append('file', file);
        // Use real customer email when logged in, otherwise anonymous
        const customerEmail = window.CUSTOMER_LOGOS_CONFIG?.email || `guest_${Date.now()}@anonymous.com`;
        formData.append('customer_email', customerEmail);
        formData.append('metadata', JSON.stringify({
            timestamp: Date.now(),
            filename: file.name
        }));

        const response = await fetch(CONFIG.SUPABASE_UPLOAD_URL, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Upload error:', errorText);
            throw new Error('Upload failed');
        }

        const data = await response.json();
        return data.logo_url;
    }

    async function confirmCustomization() {
        // Validation: Terms Checkbox
        if (elements.termsCheckbox && !elements.termsCheckbox.checked) {
            const notice = elements.termsCheckbox.closest('.logo-customization__notice');
            if (notice) {
                notice.classList.remove('logo-customization__notice--error');
                void notice.offsetWidth; // Force reflow
                notice.classList.add('logo-customization__notice--error');
                notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        const btn = elements.confirmBtn;
        const originalText = btn.innerHTML;
        btn.disabled = true;
        btn.innerHTML = `<div class="logo-customization__spinner"></div> ${window.LOGO_STRINGS.processing}`;

        try {
            const itemsToAdd = [];

            // Ensure logo is uploaded (should already be done in Step 2)
            if (!state.uploadedLogoUrl && state.logoFile) {
                state.uploadedLogoUrl = await uploadLogoToSupabase(state.logoFile);
            }

            const logoUrl = state.uploadedLogoUrl || state.logoUrl || '';

            // 1. Service Product (Embroidery)
            if (window.LOGO_CONFIG.serviceProductVariantId) {
                const fileName = state.logoFile ? state.logoFile.name : (state.logoUrl ? state.logoUrl.split('/').pop() : 'N/A');

                if (state.customization.mode === 'common') {
                    // COMMON MODE
                    const totalItems = state.selectedItems.reduce((sum, item) => sum + item.totalQty, 0);
                    const positions = state.customization.commonPositions;
                    const totalQty = totalItems * positions.length;
                    const productList = state.selectedItems.map(item => `${item.title} (x${item.totalQty})`).join('; ');

                    if (totalQty > 0) {
                        itemsToAdd.push({
                            id: window.LOGO_CONFIG.serviceProductVariantId,
                            quantity: totalQty,
                            properties: {
                                '_CustomizationType': 'Logo Embroidery',
                                '_CustomizationMode': 'Common',
                                'Artigos': productList,
                                'Posições': positions.join(', '),
                                'Nome do Ficheiro': fileName,
                                '_Logótipo URL': logoUrl,
                                'Notas': state.customization.notes
                            }
                        });
                    }
                } else {
                    // INDIVIDUAL MODE
                    state.selectedItems.forEach(group => {
                        const positions = state.customization.itemPositions[group.productId] || [];
                        if (positions.length > 0) {
                            const qty = group.totalQty * positions.length;
                            itemsToAdd.push({
                                id: window.LOGO_CONFIG.serviceProductVariantId,
                                quantity: qty,
                                properties: {
                                    '_CustomizationType': 'Logo Embroidery',
                                    '_CustomizationMode': 'Individual',
                                    'Artigo': `${group.title} (x${group.totalQty})`,
                                    'Posições': positions.join(', '),
                                    'Nome do Ficheiro': fileName,
                                    '_Logótipo URL': logoUrl,
                                    'Notas': state.customization.notes
                                }
                            });
                        }
                    });
                }
            }

            // 2. Film Product (One-time fee)
            // Skip film/setup product for existing logos (already paid)
            if (window.LOGO_CONFIG.filmProductVariantId && !state.isExistingLogo) {
                const fileName = state.logoFile ? state.logoFile.name : (state.logoUrl ? state.logoUrl.split('/').pop() : 'N/A');
                itemsToAdd.push({
                    id: window.LOGO_CONFIG.filmProductVariantId,
                    quantity: 1,
                    properties: {
                        '_CustomizationType': 'Film Setup',
                        'Nome do Ficheiro': fileName,
                        '_Logótipo URL': logoUrl
                    }
                });
            }

            // 3. Add to Cart via AJAX
            if (itemsToAdd.length > 0) {
                const cartDrawer = document.querySelector('cart-drawer');
                const payload = { items: itemsToAdd };

                // Add sections to render if cart drawer exists
                if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
                    payload.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
                    payload.sections_url = window.location.pathname;
                }

                const response = await fetch(CONFIG.CART_ADD_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) throw new Error('Falha ao adicionar ao carrinho');

                const data = await response.json();

                // 4. Update button to success state (green)
                btn.classList.add('logo-customization__btn--success');
                btn.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.6668 5L7.50016 14.1667L3.3335 10" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg> ${window.LOGO_STRINGS.successAdded}`;

                // Show "Add New Logo" button
                if (elements.addNewLogoBtn) {
                    elements.addNewLogoBtn.classList.remove('hidden');
                }

                // 5. Update cart drawer using theme's native method
                if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
                    cartDrawer.renderContents(data);
                } else {
                    // Fallback: update cart count and redirect
                    const cartCount = document.querySelector('.cart-count-bubble span');
                    if (cartCount) {
                        try {
                            const cartCtx = await fetch('/cart.js').then(r => r.json());
                            cartCount.textContent = cartCtx.item_count;
                        } catch (e) { console.error(e); }
                    }
                    window.location.href = '/cart';
                }
            }

        } catch (error) {
            console.error('Error:', error);
            alert(window.LOGO_STRINGS.errorAdding);
            btn.disabled = false;
            btn.innerHTML = originalText;
            btn.classList.remove('logo-customization__btn--success');
        }
    }

    // Init
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    /* ============================================================================
       CALIBRATION MODE
       ============================================================================ */

    function injectCalibrationUI() {
        return; // Disabled by user request
        // 1. Calibration Toggle Button (IN STEP 3 HEADER)
        // 1. Calibration Toggle Button (IN STEP 3 HEADER)
        // Instead of next to Refresh (Step 1), put it in Step 3 Panel Header?
        // User requested: "apenas na página das localizações"
        // Let's create it and hide it initially, managing visibility in goToStep.

        // Remove old if exists
        const oldBtn = document.getElementById('calibration-toggle');
        if (oldBtn) oldBtn.remove();

        const step3Panel = document.querySelector('[data-step="3"] .logo-customization__panel-header');
        if (step3Panel && !document.getElementById('calibration-toggle')) {
            const btn = document.createElement('label'); // Use label for checkbox feel or just button
            btn.id = 'calibration-toggle-container';
            btn.style.cssText = 'display: inline-flex; align-items: center; margin-left: 10px; font-size: 13px; color: #333; cursor: pointer;';

            const input = document.createElement('input');
            input.type = 'checkbox';
            input.id = 'calibration-toggle';
            input.style.marginRight = '5px';

            btn.appendChild(input);
            btn.appendChild(document.createTextNode('Modo Calibração 🛠'));

            // Append to header title or description?
            step3Panel.appendChild(btn);
            elements.calibrationToggle = input;

            input.addEventListener('change', toggleCalibrationMode);
        } else {
            // Fallback if Step 3 panel not found (shouldn't happen)
            console.warn('Step 3 Panel not found for calibration toggle');
        }

        // 2. Info Tooltip (Floating)
        if (!document.querySelector('.logo-customization__calibration-info')) {
            const info = document.createElement('div');
            info.className = 'logo-customization__calibration-info hidden';
            document.body.appendChild(info);
            elements.calibrationInfo = info;
        }

        // 3. Controls Panel (JSON Output)
        if (!document.querySelector('.logo-customization__calibration-controls')) {
            const panel = document.createElement('div');
            panel.className = 'logo-customization__calibration-controls';
            panel.innerHTML = `
            <h4>Calibração</h4>
            <p style="font-size:11px; color:#666; margin:0 0 5px">Arrastar para mover. Scroll ou Slider p/ tamanho.</p>
            
            <div style="margin-bottom: 10px;">
                <label style="font-size:10px; display:block">Tamanho (Width %): <span id="calib-size-val">--</span></label>
                <input type="range" id="calib-size-slider" min="2" max="50" step="0.5" style="width:100%">
            </div>

            <textarea id="calib-output" style="width:100%; height:80px; font-size:10px; font-family:monospace;"></textarea>
            <button type="button" id="calib-copy" style="margin-top:5px; width:100%;">Copiar Config</button>
        `;
            document.body.appendChild(panel);
            elements.calibrationPanel = panel;
            elements.calibrationOutput = panel.querySelector('#calib-output');
            elements.calibrationSizeSlider = panel.querySelector('#calib-size-slider');
            elements.calibrationSizeVal = panel.querySelector('#calib-size-val');

            panel.querySelector('#calib-copy').addEventListener('click', () => {
                elements.calibrationOutput.select();
                document.execCommand('copy');
                alert('Copiado!');
            });

            elements.calibrationSizeSlider.addEventListener('input', (e) => {
                if (state.drag.element) {
                    const val = e.target.value;
                    state.drag.element.style.width = val + '%';
                    state.drag.element.style.height = val + '%'; // Keep square
                    elements.calibrationSizeVal.textContent = val + '%';

                    // Update config
                    const name = state.drag.element.dataset.position;
                    const posConfig = CONFIG.POSITIONS.find(p => p.name === name);
                    if (posConfig) posConfig.width = parseFloat(val);

                    updateCalibrationOutput();
                }
            });
        }
    }

    function toggleCalibrationMode(e) {
        // If passed generic event
        if (e && e.target) {
            state.calibrationMode = e.target.checked;
        } else {
            state.calibrationMode = !state.calibrationMode;
        }

        console.log('Calibration Mode Toggled:', state.calibrationMode);

        const workspace = document.querySelector('.logo-customization__workspace');
        if (workspace) workspace.classList.toggle('calibration-mode', state.calibrationMode);

        if (elements.mockupContainer) elements.mockupContainer.classList.toggle('calibration-mode', state.calibrationMode);

        if (elements.calibrationPanel) {
            elements.calibrationPanel.classList.toggle('visible', state.calibrationMode);
        }

        // Re-render hotspots to ensure all are visible if needed, 
        // though CSS handles visibility via opacity mostly.
        // But we want ALL available positions defined in config to be visible.
        renderMockupHotspots();

        if (state.calibrationMode) {
            enableDragEvents();
            updateCalibrationOutput();
        } else {
            disableDragEvents();
        }
    }

    // Drag Event Handlers
    function handleMouseDown(e) {
        if (!state.calibrationMode) return;

        const isHotspot = e.target.classList.contains('logo-customization__hotspot');
        const isHandle = e.target.classList.contains('logo-customization__hotspot-handle');

        if (!isHotspot && !isHandle) return;

        e.preventDefault();
        e.stopPropagation();

        state.drag.active = true;
        state.drag.isResizing = isHandle;
        state.drag.element = isHandle ? e.target.closest('.logo-customization__hotspot') : e.target;
        state.drag.startX = e.clientX;
        state.drag.startY = e.clientY;

        const rect = state.drag.element.getBoundingClientRect();
        const containerRect = elements.mockupContainer.getBoundingClientRect();

        state.drag.containerWidth = containerRect.width;
        state.drag.containerHeight = containerRect.height;

        // Current percentages
        state.drag.startLeft = parseFloat(state.drag.element.style.left);
        state.drag.startTop = parseFloat(state.drag.element.style.top);
        state.drag.startWidth = parseFloat(state.drag.element.style.width);

        state.drag.element.classList.add('active');

        // Update Slider
        const currentWidth = parseFloat(e.target.style.width) || 10;
        if (elements.calibrationSizeSlider) {
            elements.calibrationSizeSlider.value = currentWidth;
            elements.calibrationSizeVal.textContent = currentWidth + '%';
        }
    }

    function handleMouseMove(e) {
        if (!state.calibrationMode || !state.drag.active) return;

        e.preventDefault();

        const dx = e.clientX - state.drag.startX;
        const dy = e.clientY - state.drag.startY;

        if (state.drag.isResizing) {
            // Calculate new width based on X movement
            // Since dx is in pixels, convert to percentage point delta
            const dWidth = (dx / state.drag.containerWidth) * 100 * 2; // *2 because it's centered (translate -50%)
            let newWidth = state.drag.startWidth + dWidth;

            // Bounds
            if (newWidth < 2) newWidth = 2;
            if (newWidth > 50) newWidth = 50;

            newWidth = Math.round(newWidth * 10) / 10;

            state.drag.element.style.width = `${newWidth}%`;
            state.drag.element.style.height = `${newWidth}%`;

            if (elements.calibrationSizeSlider) {
                elements.calibrationSizeSlider.value = newWidth;
                elements.calibrationSizeVal.textContent = newWidth + '%';
            }
        } else {
            // Movement logic
            const dLeft = (dx / state.drag.containerWidth) * 100;
            const dTop = (dy / state.drag.containerHeight) * 100;

            let newLeft = state.drag.startLeft + dLeft;
            let newTop = state.drag.startTop + dTop;

            newLeft = Math.round(newLeft * 10) / 10;
            newTop = Math.round(newTop * 10) / 10;

            state.drag.element.style.left = `${newLeft}%`;
            state.drag.element.style.top = `${newTop}%`;
        }

        // Update tooltip
        updateCalibrationTooltip(state.drag.element, parseFloat(state.drag.element.style.top), parseFloat(state.drag.element.style.left));
    }

    function handleMouseUp(e) {
        if (!state.calibrationMode || !state.drag.active) return;

        // Update Config Logic (In memory only)
        const name = state.drag.element.dataset.position;
        const posConfig = CONFIG.POSITIONS.find(p => p.name === name);
        if (posConfig) {
            posConfig.top = parseFloat(state.drag.element.style.top);
            posConfig.left = parseFloat(state.drag.element.style.left);
            posConfig.width = parseFloat(state.drag.element.style.width); // In case resized
        }

        state.drag.element.classList.remove('active');
        state.drag.active = false;
        state.drag.isResizing = false;
        state.drag.element = null;

        updateCalibrationOutput();
    }

    function handleWheel(e) {
        if (!state.calibrationMode) return;
        if (!e.target.classList.contains('logo-customization__hotspot')) return;

        e.preventDefault();

        const currentWidth = parseFloat(e.target.style.width) || 10;
        const delta = e.deltaY > 0 ? -1 : 1; // Scroll UP = bigger

        let newWidth = currentWidth + delta;
        if (newWidth < 2) newWidth = 2;
        if (newWidth > 50) newWidth = 50;

        e.target.style.width = `${newWidth}%`;
        e.target.style.height = `${newWidth}%`; // Maintain square for now

        // Update slider if visible
        if (elements.calibrationSizeSlider) {
            elements.calibrationSizeSlider.value = newWidth;
            elements.calibrationSizeVal.textContent = newWidth + '%';
        }

        // Update config
        const name = e.target.dataset.position;
        const posConfig = CONFIG.POSITIONS.find(p => p.name === name);
        if (posConfig) {
            posConfig.width = newWidth;
        }

        updateCalibrationOutput();
    }

    function updateCalibrationTooltip(el, top, left) {
        if (!elements.calibrationInfo) return;

        const rect = el.getBoundingClientRect();
        elements.calibrationInfo.style.top = `${rect.top - 30}px`;
        elements.calibrationInfo.style.left = `${rect.left + (rect.width / 2)}px`;
        elements.calibrationInfo.textContent = `T:${top}% L:${left}% W:${el.style.width}`;
        elements.calibrationInfo.classList.remove('hidden');
    }

    function updateCalibrationOutput() {
        if (!elements.calibrationOutput) return;
        // Clean output: remove unnecessary keys or keep consistent
        const output = CONFIG.POSITIONS.map(p => ({
            name: p.name,
            group: p.group || 'upper',
            dimensions: p.dimensions || '10x10cm',
            top: p.top,
            left: p.left,
            width: p.width
        }));
        elements.calibrationOutput.value = JSON.stringify(output, null, 2);
    }

    function enableDragEvents() {
        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('wheel', handleWheel, { passive: false });
    }

    function disableDragEvents() {
        document.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('wheel', handleWheel);
        if (elements.calibrationInfo) elements.calibrationInfo.classList.add('hidden');
    }

    /**
     * Force scroll to the absolute top of the page (Round 14)
     * Target the main header to ensure the title is visible.
     */
    function forceScrollToTop() {
        // Immediate jump to absolute top (0,0) only if not already there
        if (window.scrollY > 10 || document.documentElement.scrollTop > 10) {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        }

        // TARGET HEADER: Focus it to force browser attention (Round 16)
        // Focus once at the start
        const appHeader = document.querySelector('.logo-customization__header');
        if (appHeader && document.activeElement !== appHeader) {
            appHeader.setAttribute('tabindex', '-1');
            appHeader.focus({ preventScroll: true });
        }

        // Smart retries: Only scroll if we somehow drifted or weren't at the top
        // AND only if the user hasn't started scrolling manually (Round 18)
        [50, 150, 300, 600, 1200, 2000, 3000].forEach(delay => {
            setTimeout(() => {
                // If user started scrolling manually, stop interfering immediately
                if (state.manualScrollOccurred) return;

                const currentScroll = window.scrollY || document.documentElement.scrollTop;
                if (currentScroll > 10) {
                    window.scrollTo(0, 0);
                    document.documentElement.scrollTop = 0;
                    document.body.scrollTop = 0;
                }
            }, delay);
        });
    }

    // Initialize
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();
